'use client';

import { motion } from 'framer-motion';
import { ArrowRight, TerminalSquare, Zap, Keyboard } from 'lucide-react';
import { SignInButton, useUser } from '@clerk/nextjs';
import Link from 'next/link';
import { cn } from '@/lib/cn';
import { TerminalDemoPlayer } from '@/components/landing/terminal/TerminalDemoPlayer';
import { FeaturesGrid } from '@/components/landing/terminal/FeaturesGrid';

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      className="font-mono text-[11px] font-semibold px-1.5 py-0.5 rounded border min-w-[20px] text-center inline-block"
      style={{
        backgroundColor: 'rgba(10, 14, 23, 0.8)',
        borderColor: 'rgba(31, 41, 55, 0.9)',
        color: 'var(--color-text)',
      }}
    >
      {children}
    </kbd>
  );
}

const SHORTCUT_GROUPS: { label: string; rows: { keys: string[]; desc: string }[] }[] = [
  {
    label: 'Navigation',
    rows: [
      { keys: ['g', 'd'], desc: 'Go to Dashboard' },
      { keys: ['g', 'r'], desc: 'Go to Research' },
      { keys: ['g', 'm'], desc: 'Go to Market Data' },
      { keys: ['['], desc: 'Previous watchlist symbol' },
      { keys: [']'], desc: 'Next watchlist symbol' },
      { keys: ['Esc'], desc: 'Back to Dashboard' },
    ],
  },
  {
    label: 'Search',
    rows: [
      { keys: ['/'], desc: 'Focus search' },
      { keys: ['⌘', 'K'], desc: 'Focus search' },
    ],
  },
  {
    label: 'Research tabs',
    rows: [
      { keys: ['1'], desc: 'Overview' },
      { keys: ['2'], desc: 'Financials' },
      { keys: ['3'], desc: 'Risk' },
    ],
  },
  {
    label: 'Market Data tabs',
    rows: [
      { keys: ['1'], desc: 'S&P 500' },
      { keys: ['2'], desc: 'Dow Jones' },
      { keys: ['3'], desc: 'NASDAQ 100' },
      { keys: ['4'], desc: 'Russell 2000' },
      { keys: ['5'], desc: 'Commodities' },
    ],
  },
];

function HeroChip({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-lg px-3 py-2 font-mono"
      style={{
        backgroundColor: 'rgba(10, 14, 23, 0.8)',
        border: '1px solid rgba(31, 41, 55, 0.8)',
      }}
    >
      <div className="text-[9px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{label}</div>
      <div className="text-xs font-semibold mt-0.5 whitespace-nowrap" style={{ color: 'var(--color-text)' }}>{value}</div>
    </div>
  );
}

export default function TerminalLandingPage() {
  const { isSignedIn } = useUser();

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <span
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-mono font-semibold uppercase tracking-wider',
                    'border'
                  )}
                  style={{
                    color: '#22D3EE',
                    backgroundColor: 'rgba(34, 211, 238, 0.08)',
                    borderColor: 'rgba(34, 211, 238, 0.25)',
                  }}
                >
                  <TerminalSquare size={14} />
                  Terminal · Pro mode
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[var(--color-text)]"
              >
                Investor-style terminal,{' '}
                <span style={{ color: 'var(--color-accent-muted)' }}>built for everyone</span>.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-6 text-lg leading-relaxed max-w-xl"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Multi-range price charts, fundamental and risk analysis, index drilldown, commodities, and
                per-ticker news — everything an active investor needs on one dense, keyboard-driven screen.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-8 flex flex-wrap gap-3"
              >
                {!isSignedIn ? (
                  <SignInButton mode="modal">
                    <button
                      className={cn(
                        'inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                        'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]'
                      )}
                    >
                      Get started free
                      <ArrowRight size={16} />
                    </button>
                  </SignInButton>
                ) : (
                  <Link
                    href="/terminal"
                    className={cn(
                      'inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                      'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]'
                    )}
                  >
                    Open Terminal
                    <ArrowRight size={16} />
                  </Link>
                )}
                <a
                  href="#features"
                  className={cn(
                    'inline-flex items-center px-5 py-3 rounded-xl text-sm font-medium transition-colors',
                    'border border-[var(--color-border)] hover:bg-[var(--color-surface-secondary)]'
                  )}
                  style={{ color: 'var(--color-text-secondary)', backgroundColor: 'transparent' }}
                >
                  Tour the features
                </a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="mt-10 grid grid-cols-3 gap-2 max-w-md"
              >
                <HeroChip label="Research" value="P/E · EPS · Risk" />
                <HeroChip label="Indices" value="S&P · Dow · NDX" />
                <HeroChip label="Macro" value="YLD · Fed · VIX" />
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-7"
            >
              <TerminalDemoPlayer />
            </motion.div>
          </div>
        </div>
      </section>

      <div id="features">
        <FeaturesGrid />
      </div>

      {/* Who is this for */}
      <section className="py-16" style={{ backgroundColor: 'var(--color-surface-secondary)' }}>
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-10 items-start max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4"
                style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)' }}
              >
                <Zap size={20} />
              </div>
              <h3 className="text-2xl font-bold text-[var(--color-text)] mb-3">For active investors</h3>
              <p className="text-base leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                You watch the market every day. You don&apos;t want six tabs open across three sites just to track
                a position. Terminal pulls quotes, charts, news, research, and macro context onto one screen so you
                can act in seconds.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4"
                style={{ backgroundColor: 'color-mix(in srgb, var(--color-accent) 15%, transparent)', color: 'var(--color-accent-muted)' }}
              >
                <TerminalSquare size={20} />
              </div>
              <h3 className="text-2xl font-bold text-[var(--color-text)] mb-3">For builders &amp; quants-at-heart</h3>
              <p className="text-base leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                Keyboard-driven, dense, monospaced. If you find consumer apps wasteful with screen space and you
                grew up on terminals, Terminal mode is going to feel like home.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Keyboard shortcuts */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto text-center mb-10"
          >
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-semibold uppercase tracking-wider mb-4 border"
              style={{
                color: 'var(--color-accent-muted)',
                backgroundColor: 'color-mix(in srgb, var(--color-accent) 8%, transparent)',
                borderColor: 'color-mix(in srgb, var(--color-accent) 25%, transparent)',
              }}
            >
              <Keyboard size={14} />
              Keyboard-first
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">
              Drive the whole thing from the keyboard.
            </h2>
            <p className="mt-3 text-base" style={{ color: 'var(--color-text-secondary)' }}>
              Press <Kbd>?</Kbd> anywhere in Terminal to bring up the full reference.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {SHORTCUT_GROUPS.map((group, i) => (
              <motion.div
                key={group.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="rounded-2xl p-5 border border-[var(--color-border-subtle)]"
                style={{ backgroundColor: 'var(--color-surface)' }}
              >
                <div
                  className="text-[10px] font-mono font-semibold uppercase tracking-wider mb-3"
                  style={{ color: 'var(--color-accent-muted)' }}
                >
                  {group.label}
                </div>
                <ul className="space-y-2.5">
                  {group.rows.map((r) => (
                    <li key={r.keys.join('+')} className="flex items-center justify-between gap-3 text-xs">
                      <span style={{ color: 'var(--color-text-secondary)' }}>{r.desc}</span>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        {r.keys.map((k, j) => (
                          <span key={j} className="flex items-center gap-1">
                            {j > 0 && <span style={{ color: 'var(--color-text-secondary)', fontSize: 10 }}>then</span>}
                            <Kbd>{k}</Kbd>
                          </span>
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              href="/learn/terminal#shortcuts"
              className="inline-flex items-center gap-2 text-sm font-medium transition-colors text-[var(--color-accent-muted)] hover:text-[var(--color-accent)]"
            >
              Read the full guide
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div
            className="rounded-3xl p-8 sm:p-12 text-center border border-[var(--color-border-subtle)] max-w-4xl mx-auto"
            style={{
              background: 'linear-gradient(135deg, color-mix(in srgb, var(--color-accent) 14%, var(--color-surface)) 0%, var(--color-surface) 100%)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--color-text)]">
              Ready to switch to Terminal mode?
            </h2>
            <p className="mt-3 text-base max-w-xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Sign in and head to the Terminal — it&apos;s included with every EquityLabs account.
            </p>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              {isSignedIn ? (
                <Link
                  href="/terminal"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
                >
                  Open Terminal
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <SignInButton mode="modal">
                  <button className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-colors bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]">
                    Get started free
                    <ArrowRight size={16} />
                  </button>
                </SignInButton>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
